import {
  Box,
  Button,
  CardActionArea,
  Chip,
  GridLegacy as Grid,
  Skeleton,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  always,
  any,
  applySpec,
  complement,
  filter,
  findIndex,
  identity,
  ifElse,
  is,
  length,
  lt,
  map,
  omit,
  pipe,
  prop,
  propEq,
  propSatisfies,
  unless,
} from "ramda";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "../../../components/Icon";
import { Msg } from "../../../components/Msg";
import { useMsg } from "../../../components/Msg/Msg";
import { H2, P } from "../../../components/Typography";
import { parametrizedRoutes, routes } from "../../../routes/constants";
import { primary25, primary500 } from "../../../theme";
import { useAuth } from "../../Authorization";
import { useMyQuery } from "../../Authorization/reactQueryWrappers";
import { useUserSettingsQuery } from "../../Settings/UserProfileSettings";
import { useStrengths } from "../../Strengths/talents";
import { SessionStepCard } from "../SessionStepCard";
import { useAreasDict } from "../areas";
import { SESSION_FIELDS } from "./constants";

const useOldAreasRecommendations = () => {
  const { areas: areasDict } = useAreasDict();
  const areasArr = Object.entries(areasDict).map(([key, value]) => ({
    key,
    name: value.label,
    label: value.label,
  }));
  return areasArr;
};

export const useArea = ({ valueArr }) => {
  // Area saved as array for some reason
  const value = valueArr?.length ? valueArr[0] : "";
  const areasArr = useOldAreasRecommendations();

  // Previously areas were saved as IDs, now they are saved as names
  // TODO: check in DB that no area is saved as number and remove
  const areaMaybe = areasArr.find((area) => area.key === value); // backward compatibility
  const areaText = areaMaybe?.name || value;

  return {
    areaText,
  };
};

const AreaTile = ({
  title,
  info,
  items,
  itemsLoading,
  selectedAreaName,
  onItemSelect,
  fallback = "",
  isTileSelected = true,
  onClick,
}) => {
  return (
    <CardActionArea
      component="div"
      disableRipple
      onClick={onClick}
      sx={{ p: 3, bgcolor: "#FCFCFD", borderRadius: "4px", minHeight: "190px" }}
    >
      <H2 sx={{ mb: 3, color: isTileSelected ? primary500 : "#344054" }}>
        <Tooltip title={info} placement="top">
          {title}
          {info && (
            <>
              &nbsp;
              <Icon name="InfoOutlined" sx={{ fontSize: "inherit" }} />
            </>
          )}
        </Tooltip>
      </H2>
      {!isTileSelected ? null : itemsLoading ? (
        <>
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </>
      ) : !items?.length ? (
        <P>{fallback}</P>
      ) : (
        <SelectableTags
          items={items}
          selected={selectedAreaName}
          onSelect={onItemSelect}
        />
      )}
    </CardActionArea>
  );
};

const SelectableTags = ({ items, selected, onSelect }) => {
  const selectedSx = {
    color: primary500,
    backgroundColor: primary25,
    border: `1px solid ${primary500}`,
  };

  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      {items.map(({ name, label }) => (
        <Chip
          key={name}
          label={label}
          sx={{
            borderRadius: "6px",
            backgroundColor: "#F9F8FF",
            ...(selected === name ? selectedSx : {}),
          }}
          onClick={(e) => {
            onSelect({ name, label });
            e.stopPropagation();
          }}
        />
      ))}
    </Box>
  );
};

export const AreaStep = ({ data, step, stepper }) => {
  const msg = useMsg();
  const { user } = useAuth();
  const navigate = useNavigate();
  const topStrengths = useStrengths({
    keys: user?.data?.strengths?.slice(0, 5),
  });
  const strengthsCount = user?.data?.strengths?.length || 0;
  const bottomStrengthKeys =
    strengthsCount >= 15
      ? user?.data?.strengths?.slice(-10)
      : strengthsCount >= 10
        ? user?.data?.strengths?.slice(-5)
        : [];
  const bottomStrengths = useStrengths({
    keys: bottomStrengthKeys,
  });
  const oldAreas = useOldAreasRecommendations();
  const areasAIQuery = useMyQuery({
    fetchDef: {
      method: "GET",
      url: `/api/latest/user-sessions/generate-recommended-growth`,
      to: pipe(
        unless(
          is(Array),
          always([]),
          // always([{ recommendation: "Communication", area: "Communication" }])
        ),
        map(
          applySpec({
            key: prop("recommendation"),
            name: prop("recommendation"),
            label: prop("recommendation"),
            // area: prop("area"),
          }),
        ),
      ),
    },
  });
  const userSettingsQuery = useUserSettingsQuery();
  const missingInToUseAi = [
    [
      !userSettingsQuery?.data?.businessStrategy,
      "business strategy (set by HR)",
    ],
    [!userSettingsQuery?.data?.position, "your position"],
    [!userSettingsQuery?.data?.aspiredCompetency, "aspired competency"],
  ];
  // const userActionRequired =
  //   !userSettingsQuery?.data?.position ||
  //   !userSettingsQuery?.data?.aspiredCompetency;
  // const missing = pipe(
  //   filter(prop(0)),
  //   map(prop(1)),
  //   join(" and "),
  //   when(always(userActionRequired), (translated) => [
  //     ...translated,
  //     "(editable by you in profile settings)",
  //   ])
  // )(missingInToUseAi);
  const allFilledIn = pipe(
    filter(prop(0)),
    complement(length),
  )(missingInToUseAi);
  const aiUnavailable = !areasAIQuery.isPending && !areasAIQuery.data?.length;
  const aiUnavailableInfo = aiUnavailable
    ? allFilledIn
      ? "We are generating personalised recommendations for you. Please, contact us if it takes too long." // should never happen if BE works as expected
      : msg("sessions.new.steps.area.recommended.info")
    : undefined;
  const goToAssessment = useCallback(() => {
    navigate(parametrizedRoutes.assessment({ returnTo: routes.session }));
  }, [navigate]);

  const tiles = useMemo(
    () => [
      {
        title: msg("sessions.new.steps.area.strengths.title"),
        items: topStrengths,
        fallback: msg("sessions.new.steps.area.fallback.title"),
        onClick: !topStrengths?.length ? goToAssessment : undefined,
      },
      {
        title: msg("sessions.new.steps.area.weaknesses.title"),
        items: bottomStrengths,
        fallback: msg("sessions.new.steps.area.fallback.title"),
        onClick: !bottomStrengths?.length ? goToAssessment : undefined,
      },
      {
        title: msg("sessions.new.steps.area.recommended.title"),
        info: aiUnavailableInfo,
        items: areasAIQuery.isPending
          ? []
          : areasAIQuery.data?.length
            ? areasAIQuery.data
            : oldAreas,
        itemsLoading: areasAIQuery.isPending,
        fallback: null,
      },
    ],
    [
      aiUnavailableInfo,
      areasAIQuery.data,
      areasAIQuery.isPending,
      bottomStrengths,
      goToAssessment,
      msg,
      oldAreas,
      topStrengths,
    ],
  );
  const valueArr = data[SESSION_FIELDS.AREA_OF_DEVELOPMENT];
  const { areaText } = useArea({ valueArr });

  const derivedSelectedTileIndex = pipe(
    findIndex(propSatisfies(any(propEq(areaText, "name")), "items")),
    ifElse(lt(0), identity, always(0)),
  )(tiles);
  const [selectedTileIndex, setSelectedTileIndex] = useState(
    derivedSelectedTileIndex,
  );
  useEffect(() => {
    setSelectedTileIndex(derivedSelectedTileIndex);
  }, [derivedSelectedTileIndex]);

  const [selectedArea, setSelected] = useState(areaText || "");
  const newArea = selectedArea?.trim();
  const field = step.fieldDefMap[SESSION_FIELDS.AREA_OF_DEVELOPMENT];
  const areaValue = (field?.map || identity)(newArea);
  const next = () => {
    stepper.handleNext({ [SESSION_FIELDS.AREA_OF_DEVELOPMENT]: areaValue });
  };

  const onItemSelect = (item) => {
    setSelected(item?.name);
  };
  const onCustomAreaChange = (value) => {
    setSelected(value);
  };

  console.log("[AreaStep.rndr]", {
    areaText,
    tiles,
    oldAreas,
    "areasAIQuery.data": areasAIQuery.data,
    data,
    selectedArea,
    step,
    stepper,
    selectedTileIndex,
    topStrengths,
    bottomStrengths,
  });

  const gridItemProps =
    tiles.length === 3
      ? { xs: 12, md: 6, lg: 4 }
      : tiles?.length === 2
        ? { xs: 12, md: 6 }
        : { xs: 12 };

  return (
    <SessionStepCard
      {...{
        step: tiles?.length === 3 ? step : omit(["perex"], step),
        stepper,
      }}
    >
      <Grid container spacing={3} sx={{ my: 3 }}>
        {tiles.map(
          ({ items, itemsLoading, title, fallback, info, onClick }, index) => (
            <Grid key={title} item {...gridItemProps}>
              <AreaTile
                title={title}
                info={info}
                items={items}
                itemsLoading={itemsLoading}
                selectedAreaName={selectedArea}
                onItemSelect={onItemSelect}
                fallback={fallback}
                isTileSelected={selectedTileIndex === index}
                onClick={() => {
                  onClick?.();
                  setSelectedTileIndex(index);
                }}
              />
            </Grid>
          ),
        )}
      </Grid>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "baseline",
          gap: 5,
        }}
      >
        <TextField
          // autoFocus
          required
          placeholder={msg("sessions.new.steps.area.customarea.placeholder")}
          name="customArea"
          size="small"
          hiddenLabel
          value={selectedArea}
          onChange={(e) => {
            onCustomAreaChange(e.target.value);
          }}
          sx={{ flex: "1 1 auto" }}
        />
        <Button
          type="submit"
          variant="contained"
          endIcon={<Icon name="ArrowForward" />}
          onClick={next}
          disabled={!newArea}
        >
          <Msg id="sessions.new.steps.area.next" />
        </Button>
      </Box>
    </SessionStepCard>
  );
};
